import React, { Dispatch, memo, SetStateAction, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from '@components/Modal/Modal';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import EntityForm from '../../../entities/entity-form/ui/EntityForm';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { useImportDistrictsLoader } from '../model/useImportDistrictsLoader';
import { useToastContext } from '@context/toastContext';
import { useLayersInteractivity } from '@hooks/map/useLayersInteractivity';
import EntityManagerCleaner from '../../../entities/EntityManagerCleaner';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName } from '@redux/selectors/selectors';
import { setSourceReloadRequired } from '@redux/map/map-reducer';
import { RoadInfrastructureEntityName } from '@api/types/types';
import { useMapLayersClickabilityContext } from '@context/mapLayersClickability';

export type DistrictImportModalProps = {
    setModalShow: Dispatch<SetStateAction<boolean>>;
    loadData: () => Promise<void>;
};

const DistrictImportModal = memo(
    ({ setModalShow, loadData: reloadList }: DistrictImportModalProps) => {
        const formEntityName = RoadInfrastructureEntityName.DistrictImportForm;
        const layerEntityName = RoadInfrastructureEntityName.District;
        const layerId = useTypedSelector(state =>
            selectMapLayerIdByEntityName(state, layerEntityName)
        );
        const { loading, saving, metadata, entity, changeForm, loadData, saveFormDataFn } =
            useImportDistrictsLoader({ entityName: formEntityName });
        const { recoverSettingsAfterDrawing } = useLayersInteractivity();
        const { recoverLayersInteractivity } = useMapLayersClickabilityContext();
        const { t } = useTranslation();
        const { addToast } = useToastContext();
        const dispatch = useDispatch();

        useEffect(() => {
            void (async function () {
                await loadData({ entityName: formEntityName });
            })();
        }, [formEntityName, loadData]);

        const saveFormHandler = useCallback(
            async (formData: FormData) => {
                const resp = await saveFormDataFn(formData);

                if (isErrorResponse(resp)) {
                    try {
                        const parsedErr =
                            typeof resp.error === 'string'
                                ? safelyParseJson(resp.error)?.error
                                : resp.error.error;
                        addToast(parsedErr, 'error');
                    } catch (e) {
                        console.log('Replan. Error parsing error response', resp, e);
                    }
                } else {
                    addToast(t('common.save-successful'), 'success');
                    recoverSettingsAfterDrawing();
                    recoverLayersInteractivity();
                    await reloadList();
                    dispatch(
                        setSourceReloadRequired({ sourceId: layerId, isReloadRequired: true })
                    );

                    //TODO can't close dialog, because resp instead of event is used here
                    // handleCloseDialog?.(event);
                }
            },
            [
                layerId,
                recoverSettingsAfterDrawing,
                recoverLayersInteractivity,
                reloadList,
                saveFormDataFn,
            ]
        );

        return (
            <Modal
                title={t('road-network.districts-import-form')}
                setVisible={setModalShow}
                narrow={true}
            >
                <LoadingBackdrop isVisible={loading || saving} transparent>
                    <EntityManagerCleaner entityName={formEntityName}>
                        <EntityForm
                            loading={loading}
                            entity={entity}
                            metadata={metadata}
                            saveFormDataFn={saveFormHandler}
                            changeFormFn={changeForm}
                            showDeleteBtn={false}
                            acceptedFileExt={{ 'application/json': ['.geojson', '.json'] }}
                            submitBtnTitle={t('road-network.districts-import-btn')}
                        />
                    </EntityManagerCleaner>
                </LoadingBackdrop>
            </Modal>
        );
    }
);

export default DistrictImportModal;
